import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getApiBaseUrl } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import './ManualQAPanel.css';

interface ManualQAPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: string;
}

interface Category {
  value: string;
  label: string;
}

const ManualQAPanel: React.FC<ManualQAPanelProps> = ({ isOpen, onClose, onSuccess, clientId }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([
    { value: 'general', label: 'General' }
  ]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch categories when panel opens
  useEffect(() => {
    if (isOpen && clientId) {
      fetchCategories();
    }
  }, [isOpen, clientId]);

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setQuestion('');
        setAnswer('');
        setCategory('general');
      }, 300); // Wait for animation to finish
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${getApiBaseUrl()}/client/${clientId}/prompt-categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories);
          setCategory(data.categories[0].value);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim() || !answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${getApiBaseUrl()}/learned-qa/${clientId}/candidates/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: question.trim(),
          answer: answer.trim(),
          category,
          auto_approve: true  // Always auto-approve
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create Q&A');
      }

      const data = await response.json();
      toast.success(data.message || 'Q&A candidate created successfully');

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating manual Q&A:', error);
      toast.error(error.message || 'Failed to create Q&A candidate');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="manual-qa-panel-overlay" onClick={onClose} />
      <div className="manual-qa-panel">
        {/* Header */}
        <div className="manual-qa-panel-header">
          <div>
            <h2 className="manual-qa-panel-title">Add Training Data</h2>
            <p className="manual-qa-panel-subtitle">
              Create a custom Q&A pair for model training
            </p>
          </div>
          <button
            className="manual-qa-panel-close"
            onClick={onClose}
            disabled={isSubmitting}
            title="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Content */}
        <div className="manual-qa-panel-content">
          <form onSubmit={handleSubmit} className="manual-qa-form">
            {/* Question Field */}
            <div className="form-group">
              <textarea
                id="question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Question *"
                rows={3}
                required
                disabled={isSubmitting}
                className="form-textarea"
              />
            </div>

            {/* Answer Field */}
            <div className="form-group">
              <textarea
                id="answer"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Answer *"
                rows={10}
                required
                disabled={isSubmitting}
                className="form-textarea"
              />
            </div>

            {/* Category Field */}
            <div className="form-group">
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={isSubmitting || loadingCategories}
                className="form-select"
              >
                {loadingCategories ? (
                  <option>Loading...</option>
                ) : (
                  categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={isSubmitting ? faCheck : faPlus} />
                {isSubmitting ? 'Creating...' : 'Add to Training'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ManualQAPanel;
